import { Plugin } from 'prosemirror-state';

import { BaseExtension } from '@lyearn/lygo-core';

import { generateNewBlockId } from './utils';

export default class BlockIdGenerator extends BaseExtension {
  get name(): string {
    return 'blockId_generator';
  }

  plugins() {
    return [
      new Plugin({
        appendTransaction: (transactions, _prevState, nextState) => {
          // Create an empty transaction in case we want to make changes
          // to the state
          const tr = nextState.tr;
          let mongoObjectId: string = '';

          // Save the current doc.
          const doc = nextState.doc;
          // Mark if the plugin made any changes to the state.
          let modified = false;
          if (
            doc.childCount <= 1 &&
            (!doc.firstChild || doc.firstChild.isTextblock) &&
            doc.textContent === ''
          ) {
            return null;
          }

          // y-prosemirror marks sync-driven transactions with `y-sync$`.
          // For those, duplicate ids mean a CRDT merge twin — delete the extra node.
          // For user edits (e.g. paste), keep the old behavior: assign a new id.
          const fromYSync = transactions.some((transaction) => transaction.getMeta('y-sync$'));

          let presentIDs = new Set();
          const reassignedDuplicateIds: Array<{ from: string; to: string; type: string }> = [];
          const deletedSyncDuplicateIds: Array<{ id: string; type: string }> = [];
          const assignedMissingIds: Array<{ to: string; type: string }> = [];
          // Collect deletes and apply from the end so positions stay valid.
          const deleteRanges: Array<{ from: number; to: number }> = [];

          doc.descendants((node, pos, _parent) => {
            // DO NOT TOUCH TEXT NODES
            if (node.type.isText) return;

            if (node.attrs.id) {
              // Detected a node with duplicate ID.
              if (presentIDs.has(node.attrs.id)) {
                if (fromYSync) {
                  deletedSyncDuplicateIds.push({ id: node.attrs.id, type: node.type.name });
                  deleteRanges.push({ from: pos, to: pos + node.nodeSize });
                  modified = true;
                } else {
                  mongoObjectId = generateNewBlockId();
                  reassignedDuplicateIds.push({
                    from: node.attrs.id,
                    to: mongoObjectId,
                    type: node.type.name,
                  });
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    id: mongoObjectId,
                  });
                  presentIDs.add(mongoObjectId);
                  modified = true;
                }
              } else {
                presentIDs.add(node.attrs.id);
              }
            } else {
              // Detected a node with no ID.
              // Give it a new ID.
              modified = true;
              mongoObjectId = generateNewBlockId();
              assignedMissingIds.push({ to: mongoObjectId, type: node.type.name });
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, id: mongoObjectId });
            }
          });

          deleteRanges
            .sort((a, b) => b.from - a.from)
            .forEach(({ from, to }) => {
              tr.delete(from, to);
            });

          // TEMP diagnostics — filter by `[WS-DEBUG]`
          if (
            reassignedDuplicateIds.length > 0 ||
            deletedSyncDuplicateIds.length > 0 ||
            assignedMissingIds.length > 0
          ) {
            console.warn('[WS-DEBUG] BlockId', {
              fromYSync,
              reassignedDuplicateIds,
              deletedSyncDuplicateIds,
              assignedMissingIds,
              childCount: doc.childCount,
              stack: new Error().stack,
            });
          }

          return modified ? tr : null;
        },
      }),
    ];
  }
}
